import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PopBaseComponent } from '../../base/pop-base-component';
import style from './contributors-detractors-table.scss?inline';
import type { ContributorsDetractorsTable } from './contributors-detractors-table.model';
import { Constants } from './contributors-detractors-table.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import { isEmptyArray, isNullish } from '../../common/utils/nullish';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber';
import { DISPLAY_TYPE } from '../contributors-detractors/contributors-detractors.constants';

/**
 * @tag pop-contributors-detractors-table-component
 * @property {ContributorsDetractorsTable} [componentObject] Contributors and Detractors table content
 * @attribute {ContributorsDetractorsTable} [data-component-object] Contributors and Detractors table content
 */
@customElement(Constants.TAG_NAME)
export class PopContributorsDetractorsTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: ContributorsDetractorsTable;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopContributorsDetractorsTableComponent.tagName;

  private renderTableHead() {
    return html`
      <tr>
        <th id="name-column" scope="col">
          <beacon-table-header variant="normal">
            ${this.componentObject?.itemType === DISPLAY_TYPE.CONTRIBUTORS
              ? Constants.CONTRIBUTORS_NAME_COLUMN_HEADER
              : Constants.DETRACTORS_NAME_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="portfolio-weight-column" scope="col" class="right-align">
          <beacon-table-header variant="normal">
            ${Constants.PORTFOLIO_WEIGHT_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="sector-column" scope="col">
          <beacon-table-header variant="normal">
            ${Constants.SECTOR_COLUMN_HEADER}
          </beacon-table-header>
        </th>
      </tr>
    `;
  }

  private renderTableBody() {
    const itemList = this.componentObject?.itemList;

    return html`
      ${itemList?.map((item) => {
        return html`
          <tr>
            <th>
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${item.name || Constants.EMPTY_CELL_VALUE}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record variant="numeral">
                ${formatToNDecimalPlaces(item.percentTotalNetAssets, 2)}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record variant="text">
                ${item.sectorName || Constants.EMPTY_CELL_VALUE}
              </beacon-table-record>
            </td>
          </tr>
        `;
      })}
    `;
  }

  render() {
    if (
      isEmptyArray(this.componentObject?.itemList) ||
      isNullish(this.componentObject?.itemList)
    ) {
      return html`
        <pop-no-data-available-component
          .message="${this.componentObject?.itemType ===
          DISPLAY_TYPE.CONTRIBUTORS
            ? Constants.CONTRIBUTORS_NO_DATA_MESSAGE
            : Constants.DETRACTORS_NO_DATA_MESSAGE}"
        ></pop-no-data-available-component>
      `;
    }
    return this.renderComponent(
      html` <section class="contributors-detractors-table-content">
        <beacon-table variant="fixed">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>`,
    );
  }
}
